// src/components/Step2RiskInflation.jsx
import React, { useEffect, useState, useRef } from "react";
import { api as axios } from "../config/apiConfig";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import { useNavigate } from "react-router-dom";
import { API_ORIGIN } from "../config/apiConfig";
import { ROUTES } from "../config/routes";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API_BASE = API_ORIGIN;   // for factors_api (has /api prefix)
const API_ROOT = API_ORIGIN;            // for get_project_estimations_api (no prefix)

// Same wizard-step indicator as every other Cost Estimation page.
const WIZARD_STEPS = [
  { key: "project", icon: "ti-file-description", label: "Project", sub: "Info & Overview" },
  { key: "budget", icon: "ti-currency-riyal", label: "Budget", sub: "Amount & Summary" },
  { key: "asset", icon: "ti-building", label: "Asset", sub: "Selection & Element" },
  { key: "element", icon: "ti-list-check", label: "Element", sub: "Main & Sub Element" },
  { key: "estimation", icon: "ti-report-money", label: "Estimation", sub: "Amount & Summary" },
  { key: "basis", icon: "ti-clipboard-text", label: "Basis of Cost", sub: "Assumption & Exclusion" },
  { key: "construction", icon: "ti-hammer", label: "Construction", sub: "Scope" },
];

// Which of the 7 wizard steps this PAGE is. Steps before this one get
// the theme's own light-green "done" tint (.wizard-item-done -- built
// into app.min.css, so this part always rendered correctly); THIS step
// gets the solid brand-green "current" highlight (.wizard-item-current,
// defined in src/styles/themeOverrides.css -- see Layout.jsx for why
// that file, not appLayout.css, is what actually loads it); steps after
// this one stay inert (.disabled).
const CURRENT_STEP_KEY = "budget";
const CURRENT_STEP_INDEX = WIZARD_STEPS.findIndex((s) => s.key === CURRENT_STEP_KEY);

// Per explicit request, every value shown on this chart (bar labels and Y
// axis ticks) reads in millions -- e.g. "1.36M" instead of "1,360,000.00"
// -- matching the millions formatting already used on the Sub Total/VAT/
// Total Sum tiles above the chart (fmtMillions inside the component).
// Module-scope here (not currency-aware) since barValueLabelPlugin below
// is also module-scope and only has the raw number to work with -- the
// tile version needs the currency symbol too, this one doesn't.
const formatChartMillions = (v) =>
  `${(Number(v || 0) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;

// Draws the numeric value above each bar, matching the reference layout.
// Clamped to the chart area so tall bars never push the label off the
// top of the canvas (that was causing labels to overlap/disappear).
const barValueLabelPlugin = {
  id: "barValueLabel",
  afterDatasetsDraw(chart) {
    const { ctx, chartArea } = chart;
    chart.data.datasets.forEach((dataset, dsIndex) => {
      const meta = chart.getDatasetMeta(dsIndex);
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (value === undefined || value === null) return;

        let y = bar.y - 6;
        const minY = chartArea.top + 12;
        if (y < minY) y = minY;

        ctx.save();
        ctx.fillStyle = "#333";
        ctx.font = "10px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillText(formatChartMillions(value), bar.x, y);
        ctx.restore();
      });
    });
  }
};

// Factor row: label | % input | readonly amount box. FIX: this used to be
// defined INSIDE Step2RiskInflation() below, which meant React saw a brand
// new `FactorRow` function (a different component "type") on every
// re-render of the parent -- so every keystroke in one of these boxes
// (which triggers a re-render via setValues) made React tear down and
// rebuild the underlying <input> DOM node from scratch, killing focus and
// the cursor position after each character typed (reported: typing
// "11.22" left the box on "11." with the cursor gone, requiring a re-click
// before every keystroke). Moving it to module scope like this gives it a
// stable identity across renders, so React just updates the existing
// input's value/props instead of remounting it -- typing now behaves like
// any other text box. Same reasoning as FactorRow in Step5Final.jsx.
//
// VAT is NOT one of these rows -- per request it lives in its own column
// in the Cost Allocation card below instead (select %, then see the
// computed amount next to it), not inside this Cost Factors (%) card.
const FactorRow = ({ label, value, onChange, amount, showAmount = true, fmt }) => (
  <div className="col-md-6">
    <div className="row align-items-center g-2">
      <label className="col-6 col-md-3 col-form-label">{label}</label>
      <div className={showAmount ? "col-6 col-md-4" : "col-12 col-md-9"}>
        <input
          type="text"
          inputMode="decimal"
          className="form-control"
          placeholder="0"
          value={value}
          onChange={onChange}
          onFocus={(e) => e.target.select()}
        />
      </div>
      {showAmount && (
        <div className="col-12 col-md-5">
          <input className="form-control bg-light" readOnly value={fmt(amount)} />
        </div>
      )}
    </div>
  </div>
);

export default function Step2RiskInflation() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [step1, setStep1] = useState({});
  const [showFactorView, setShowFactorView] = useState(false);

  // Duration -- per request, this is now a real, independently-selectable
  // dropdown here too (same Duration_Master-backed pattern already used
  // on Step5Final.jsx), rather than a read-only display of Step 1's saved
  // value: Step 1's own Duration field was removed, so step1.duration is
  // never populated for a new project. FIX: this used to default from
  // localStorage.getItem("duration"), but that's whatever a PREVIOUS
  // project last had selected -- for a new project it silently
  // pre-selected a stale value (e.g. "48 Months") instead of showing
  // "Select". Per request, this always starts blank so the user picks it
  // explicitly.
  const [duration, setDuration] = useState("");
  const [durations, setDurations] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_ROOT}/duration`)
      .then((res) => setDurations(res.data?.data || []))
      .catch((err) => console.error("Failed to load durations", err));
  }, []);

  // Standardized alert presentation (matches Step1ProjectInfo.jsx /
  // Step4SubElement.jsx): validation/server errors render as an inline
  // red "alert-danger" banner right below the wizard tabs, and success
  // renders as the app's one themed dark-teal confirmation modal instead
  // of a native alert() popup. Navigation that used to fire immediately
  // alongside the alert now waits until the user dismisses the modal.
  const [serverError, setServerError] = useState(null);
  // "Start Estimation" success confirmation -- per request, back to the
  // app's standard themed dark-teal modal (matching every other wizard
  // page's success popup) instead of a native browser alert(). Holds
  // the backend's own message so it reads e.g. "Budget updated
  // successfully" rather than a hard-coded string.
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [savedModalMessage, setSavedModalMessage] = useState("");
  // FIX: per request, clicking Save shouldn't pop up anything the user has
  // to dismiss -- only Next/"Start Estimation" should still show the
  // themed confirmation modal (it's the natural "confirm before moving
  // on" moment there). Save now shows this inline banner instead, same
  // pattern as Step1ProjectInfo.jsx's own Save.
  const [saveMessage, setSaveMessage] = useState(null);
  // "Last saved <time>" next to the Save button -- same convention as
  // Step5Final.jsx's own lastSavedInfo, now applied to every wizard page.
  const [lastSavedTime, setLastSavedTime] = useState(null);

  // Currency symbol for the "Cost (...)" column headers below -- fetched
  // from the Currency Master table (GET /currency, same endpoint
  // Step4SubElement.jsx already uses) instead of the hard-coded "SAR"
  // literal, so changing that table's Currency_Symbol value changes what
  // shows here with no code change. Starts blank rather than defaulting
  // to a guessed symbol, so nothing hard-coded ever briefly shows.
  const [currencySymbol, setCurrencySymbol] = useState("");

  useEffect(() => {
    axios
      .get(`${API_ROOT}/currency`)
      .then((res) => setCurrencySymbol(res.data?.data?.Currency_Symbol || ""))
      .catch((err) => console.error("Failed to load currency", err));
  }, []);

  // VAT (%) is a dropdown again, per request -- same idea as the Cost
  // Factors dropdowns, just backed by its own dedicated Vat_Config table
  // (see GET /vat-config / vat_config_dropdown_migration.sql) instead of
  // living inside Cost_Factors. This fetch loads every selectable
  // percentage into vatOptions for the dropdown, and pre-selects whichever
  // row is flagged Is_Default (falling back to the first option) -- but
  // only if this project doesn't already have its own saved VAT_Percent.
  // Both this fetch and the "prefill from the project's previously-saved
  // budget" effect below fire on mount with no guaranteed order, so this
  // ref (set by that other effect when it applies a real saved value)
  // stops this one from clobbering it if this fetch resolves later.
  const [vatOptions, setVatOptions] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_ROOT}/vat-config`)
      .then((res) => {
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        setVatOptions(rows);
        if (rows.length && !projectVatLoadedRef.current) {
          const def = rows.find((r) => r.Is_Default) || rows[0];
          setValues((prev) => ({ ...prev, VAT_Percent: Number(def.VAT_Percent) }));
        }
      })
      .catch((err) => console.error("Failed to load VAT config", err));
  }, []);

  const [values, setValues] = useState({
    Budget: 0,
    Design: 0,
    Supervision: 0,
    Other_Project: 0,

    // Per request, each Factor (%) field now defaults to "0.00" (shown in
    // the box) instead of blank, now that these are free-text inputs
    // rather than a "Select..." dropdown.
    Prelims_Percent: "0.00",
    Risk_Percent: "0.00",
    Inflation_Percent: "0.00",
    Contingency_Percent: "0.00",
    Location_Percent: "0.00",
    Tender_Percent: "0.00",
    Escalation_Percent: "0.00",
    VAT_Percent: 0,

    Prelims_Amount: 0,
    Risk_Amount: 0,
    Inflation_Amount: 0,
    Contingency_Amount: 0,
    Location_Amount: 0,
    Tender_Amount: 0,
    Escalation_Amount: 0,

    Construction: 0,
    Factor_Cost: 0,
    Subtotal: 0,
    Vat_Amount: 0,
    Total_Sum: 0
  });

  // Load Step‑1, plus restore any in-progress local draft. Used to also
  // load GET /get_cost_factors here to populate each Factor (%) dropdown's
  // option list -- per request, those dropdowns were replaced with plain
  // editable inputs (see FactorRow below), so that fetch is gone too.
  useEffect(() => {
    const s1 = {
      projectID: localStorage.getItem("projectID"),
      projectName: localStorage.getItem("projectName"),
      estimatedBy: localStorage.getItem("estimatedBy"),
      estimationDate: localStorage.getItem("estimationDate"),
      operatingCompany: localStorage.getItem("operatingCompany"),
      intro: localStorage.getItem("intro"),
      inflation: localStorage.getItem("inflation"),
      financial: localStorage.getItem("financial"),
      contingency: localStorage.getItem("contingency"),
      risks: localStorage.getItem("risks"),
      opportunities: localStorage.getItem("opportunities"),
      class: localStorage.getItem("class"),
      C_ID: localStorage.getItem("C_ID"),
      duration: localStorage.getItem("duration")
    };

    setStep1(s1);

    const saved = JSON.parse(localStorage.getItem("step2Values") || "null");
    if (saved) {
      setValues((prev) => ({ ...prev, ...saved }));
    }
  }, []);

  // Set to true when the project's own saved VAT_Percent gets applied
  // below, so the VAT-config default fetch above never overwrites it if
  // that fetch happens to resolve after this one -- see the comment there.
  const projectVatLoadedRef = useRef(false);

  // Prefill from the project's previously-saved budget (Flag='B'), if any.
  useEffect(() => {
    const projectId = localStorage.getItem("projectID");
    if (!projectId) return;

    axios
      .get(`${API_ROOT}/get_project_estimations_api?projectId=${encodeURIComponent(projectId)}`)
      .then((res) => {
        // Fix: this Duration dropdown (like every other field on this
        // page) needs to reflect the project's own already-saved value on
        // reopen -- previously nothing here ever called setDuration, so it
        // always showed "Select" for an existing project no matter what
        // was actually saved, and clicking Save without noticing/
        // reselecting silently blanked out Cost_Budget_Estimation.duration
        // (the UPDATE always writes whatever `duration` currently is, with
        // no "only if truthy" guard like the projects.Duration sync has).
        // The saved value lives at the top level of this response (see
        // get_project_estimations_api in app.py -- "duration", a sibling
        // of "budget"/"estimated", not a field on `budget` itself), same
        // place EstimationSummary.jsx/DashboardExecutiveSummary.jsx's own
        // "Project Duration" tiles already read it from.
        if (res.data?.success && res.data?.duration) {
          setDuration(res.data.duration);
        }

        const b = res.data?.budget;
        if (!res.data?.success || !b) return;
        if (b.VAT_Percent) projectVatLoadedRef.current = true;
        setValues((prev) => ({
          ...prev,
          Budget: Number(b.Budget) || 0,
          Design: Number(b.Design) || 0,
          Supervision: Number(b.Supervision) || 0,
          Other_Project: Number(b.Other_Project) || 0,
          Prelims_Percent: b.Prelims_Percent ? String(b.Prelims_Percent) : prev.Prelims_Percent,
          Risk_Percent: b.Risk_Percent ? String(b.Risk_Percent) : prev.Risk_Percent,
          Inflation_Percent: b.Inflation_Percent ? String(b.Inflation_Percent) : prev.Inflation_Percent,
          Contingency_Percent: b.Contingency_Percent ? String(b.Contingency_Percent) : prev.Contingency_Percent,
          Location_Percent: b.Location_Percent ? String(b.Location_Percent) : prev.Location_Percent,
          Tender_Percent: b.Tender_Percent ? String(b.Tender_Percent) : prev.Tender_Percent,
          Escalation_Percent: b.Escalation_Percent ? String(b.Escalation_Percent) : prev.Escalation_Percent,
          VAT_Percent: b.VAT_Percent ? Number(b.VAT_Percent) : prev.VAT_Percent
        }));
      })
      .catch((err) => console.error("Failed to load saved budget for prefill:", err));
  }, []);

  // Auto calculations
  useEffect(() => {
    const {
      Budget,
      Design,
      Supervision,
      Other_Project,
      Prelims_Percent,
      Risk_Percent,
      Inflation_Percent,
      Contingency_Percent,
      Location_Percent,
      Tender_Percent,
      Escalation_Percent,
      VAT_Percent
    } = values;

    const calc = (p) => (Budget * p) / 100;

    const Prelims_Amount = calc(Prelims_Percent || 0);
    const Risk_Amount = calc(Risk_Percent || 0);
    const Inflation_Amount = calc(Inflation_Percent || 0);
    const Contingency_Amount = calc(Contingency_Percent || 0);
    const Location_Amount = calc(Location_Percent || 0);
    const Tender_Amount = calc(Tender_Percent || 0);
    const Escalation_Amount = calc(Escalation_Percent || 0);

    const Factor_Cost =
      Prelims_Amount +
      Risk_Amount +
      Inflation_Amount +
      Contingency_Amount +
      Location_Amount +
      Tender_Amount +
      Escalation_Amount;

    const Construction = Budget;
    const Subtotal = Construction + Factor_Cost + Design + Supervision + Other_Project;
    const Vat_Amount = (Subtotal * (VAT_Percent || 0)) / 100;
    const Total_Sum = Subtotal + Vat_Amount;

    setValues((prev) => ({
      ...prev,
      Prelims_Amount,
      Risk_Amount,
      Inflation_Amount,
      Contingency_Amount,
      Location_Amount,
      Tender_Amount,
      Escalation_Amount,
      Factor_Cost,
      Construction,
      Subtotal,
      Vat_Amount,
      Total_Sum
    }));
  }, [
    values.Budget,
    values.Design,
    values.Supervision,
    values.Other_Project,
    values.Prelims_Percent,
    values.Risk_Percent,
    values.Inflation_Percent,
    values.Contingency_Percent,
    values.Location_Percent,
    values.Tender_Percent,
    values.Escalation_Percent,
    values.VAT_Percent
  ]);

  // Per request, each Factor (%) row is now a free-text percent entry
  // instead of a dropdown limited to whatever values happen to exist in
  // the Cost_Factors table -- the user can type any percentage. Same
  // "digits with an optional single decimal point" validation approach as
  // the General Costs amount fields above (clampCost/COST_INPUT_PATTERN),
  // just capped at 3 integer digits / 2 decimals (0–999.99%) instead of a
  // currency-sized range, since these are percentages, not amounts.
  const PERCENT_INPUT_PATTERN = /^\d{0,3}(\.\d{0,2})?$/;
  const handlePercentChange = (percentKey) => (e) => {
    const raw = e.target.value;
    if (raw !== "" && !PERCENT_INPUT_PATTERN.test(raw)) return;
    setValues({ ...values, [percentKey]: raw });
  };

  // Save budget. FIX: this used to decide "Save" vs "Start Estimation"
  // behavior by reading a `saveIntent` state variable that the caller set
  // via setSaveIntent() right before calling saveBudget() -- but React
  // state updates aren't applied synchronously, so this function (already
  // bound from the previous render) still saw the OLD saveIntent value on
  // the very click that changed it. That's what forced clicking "Start
  // Estimation" twice: click 1 silently ran the stale "Save" branch, and
  // only click 2 (once the re-render had caught up) actually ran the
  // "next" branch. Taking `intent` as a plain argument instead sidesteps
  // React state timing entirely -- the caller's own click is the only
  // source of truth, so it's correct on the very first click.
  const saveBudget = async (intent) => {
    // Per request: if the user tries to Save or Start Estimation without
    // picking a Duration, show the same inline "alert-danger" banner this
    // page already uses for server errors (right below the wizard tabs) --
    // not a native popup/alert() in a separate window. Bail out before
    // building the payload/calling the API at all, same as any other
    // client-side validation would.
    if (!duration) {
      setServerError("Please select a Duration before saving.");
      setSaveMessage(null);
      return;
    }

    const payload = {
      projectid: step1.projectID || step1.C_ID || null,
      project_name: step1.projectName || "",
      estimation_date: step1.estimationDate || "",
      estimated_by: step1.estimatedBy || "",
      operating_company: step1.operatingCompany || "",
      project_introduction: step1.intro || "",
      inflation_forecast: step1.inflation || "",
      risk: step1.risks || "",
      financial_overview: step1.financial || "",
      contingency: step1.contingency || "",
      opportunities: step1.opportunities || "",
      // Per request, sent from this page's own Duration dropdown (see
      // `duration` state above) now, not step1.duration -- Step 1's
      // Duration field was removed, so step1.duration is never populated.
      duration: duration || "",

      budget: values.Budget,
      design: values.Design,
      supervision: values.Supervision,
      other_project: values.Other_Project,

      construction: values.Construction,
      subtotal: values.Subtotal,
      vat_amount: values.Vat_Amount,
      total_sum: values.Total_Sum,

      flag: "B",

      prelims_percent: values.Prelims_Percent,
      risk_percent: values.Risk_Percent,
      inflation_percent: values.Inflation_Percent,
      contingency_percent: values.Contingency_Percent,
      location_percent: values.Location_Percent,
      tender_percent: values.Tender_Percent,
      escalation_percent: values.Escalation_Percent,
      VAT_percent: values.VAT_Percent,

      prelims_amount: values.Prelims_Amount,
      risk_amount: values.Risk_Amount,
      inflation_amount: values.Inflation_Amount,
      contingency_amount: values.Contingency_Amount,
      location_amount: values.Location_Amount,
      tender_amount: values.Tender_Amount,
      escalation_amount: values.Escalation_Amount,

      factor_cost: values.Factor_Cost
    };

    setServerError(null);
    setSaveMessage(null);
    setLoading(true);

    try {
      const res = await axios.post(`${API_BASE}/save_cost_budget`, payload);

      if (res.data?.success) {
        localStorage.setItem("step2Values", JSON.stringify(values));
        if (intent === "next") {
          // Per request: Start Estimation is a one-shot action, not
          // "Save" -- it doesn't touch the Save button's "Last saved"
          // feedback at all. It shows the app's standard themed
          // confirmation modal; navigation to Step 3 happens once the
          // user dismisses it via OK -- see handleSavedModalOk below.
          setSavedModalMessage(res.data?.message || "Budget inserted successfully.");
          setShowSavedModal(true);
        } else {
          // Save (not Next/"Start Estimation") -- no popup to dismiss,
          // just an inline confirmation on this same page.
          setSaveMessage("Budget saved.");
          setLastSavedTime(new Date());
        }
      } else {
        setServerError(res.data?.message || res.data?.error || "Not insert data.");
      }
    } catch (err) {
      setServerError(err.response?.data?.message || err.response?.data?.error || "Error inserting budget.");
    }

    setLoading(false);
  };

  const handleSavedModalOk = () => {
    setShowSavedModal(false);
    navigate(ROUTES.COST_ESTIMATION_STEP3);
  };

  const chartData = {
    // Per explicit request ("Change Construction to Execution throughout
    // the Step 2 Proposed Cost Summary UI"): this bar's label reads
    // "Execution" -- matching the "Execution Cost" field it's built from
    // (values.Construction below is still the same underlying state name,
    // just displayed under the label already used for it everywhere else
    // on this page).
    labels: [
      "Execution",
      "Design",
      "Supervision",
      "Other Project Cost",
      "Prelims",
      "Risk",
      "Inflation",
      "Contingency",
      "Location",
      "Tender Index",
      "Escalation"
    ],
    datasets: [
      {
        label: "Cost",
        data: [
          values.Construction,
          values.Design,
          values.Supervision,
          values.Other_Project,
          values.Prelims_Amount,
          values.Risk_Amount,
          values.Inflation_Amount,
          values.Contingency_Amount,
          values.Location_Amount,
          values.Tender_Amount,
          values.Escalation_Amount
        ],
        backgroundColor: "#155F82"
      }
    ]
  };

  // Leave headroom above the tallest bar so its value label always has
  // room to render instead of being clipped against the top of the chart.
  const chartMaxValue = Math.max(
    values.Construction,
    values.Design,
    values.Supervision,
    values.Other_Project,
    values.Prelims_Amount,
    values.Risk_Amount,
    values.Inflation_Amount,
    values.Contingency_Amount,
    values.Location_Amount,
    values.Tender_Amount,
    values.Escalation_Amount,
    1
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 24 }
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    scales: {
      // Per explicit request: X axis titled "Cost Breakdown (<symbol>)"
      // (it's the categories -- Execution, Design, Supervision, etc --
      // that make up the cost breakdown), Y axis titled "Cost (<symbol>)"
      // (the value scale). <symbol> is the same currencySymbol state
      // already used for the "Cost (...)" column headers elsewhere on
      // this page, so it stays in sync with the Currency Master table
      // instead of a hard-coded "SAR"/"﷼" literal.
      x: {
        ticks: { maxRotation: 45, minRotation: 45, font: { size: 10 } },
        grid: { display: false },
        title: { display: true, text: "Cost Breakdown", font: { size: 12, weight: "bold" } }
      },
      y: {
        suggestedMax: chartMaxValue * 1.2,
        title: { display: true, text: `Cost (${currencySymbol})`, font: { size: 12, weight: "bold" } },
        ticks: { callback: (val) => formatChartMillions(val), font: { size: 10 } },
        grid: { color: "rgba(0,0,0,0.06)" }
      }
    }
  };

  // Per request, every displayed cost value now shows thousands
  // separators ("10,000.00" instead of "10000.00") -- was a plain
  // toFixed(2), which is what "10000.00" everywhere in the mockup
  // screenshot was coming from.
  const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Per explicit request: the Sub Total/VAT/Total Sum tiles in the
  // "Proposed Cost Summary" row show their value in millions with the
  // currency symbol (e.g. "﷼ 1.36M" instead of "1,360,000.00") -- large
  // project totals read faster that way. currencySymbol is the same
  // dynamic value (from the Currency Master table) already used for the
  // "Cost (...)" headers elsewhere on this page, not a hard-coded "﷼"
  // literal. Only used for those 3 tiles; every other amount on this page
  // (Cost Allocation/Budget Summary fields, the chart's own value
  // labels/axis ticks) keeps the existing full-number fmt(). Per a later
  // request, the symbol now leads the value (matching the Dashboard's
  // Executive Summary tiles) instead of trailing it.
  const fmtMillions = (n) =>
    `${currencySymbol ? `${currencySymbol} ` : ""}${(Number(n || 0) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;

  // Same conversion EstimationSummary.jsx already uses to show Project
  // Duration in years instead of the raw "36 Months" (or "60 Months (5
  // Years)") string Duration_Master stores -- kept as an identical copy
  // here since the two files don't share a components/utils module for
  // it yet.
  const formatDurationYears = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "—";
    const str = String(raw).trim();
    if (!str) return "—";
    const parenYearsMatch = str.match(/\((\d+(\.\d+)?)\s*years?\)/i);
    if (parenYearsMatch) {
      const y = Number(parenYearsMatch[1]);
      return `${y} Year${y === 1 ? "" : "s"}`;
    }
    if (/year/i.test(str) && !/month/i.test(str)) return str;
    const match = str.match(/(\d+(\.\d+)?)/);
    if (!match) return str;
    const months = Number(match[1]);
    if (!months) return str;
    const years = months / 12;
    const roundedYears = Number.isInteger(years) ? years : Math.round(years * 10) / 10;
    return `${roundedYears} Year${roundedYears === 1 ? "" : "s"}`;
  };

  // The 4 "General Costs" boxes (Budget/Design/Supervision/Other Project)
  // are the only EDITABLE cost fields on this page -- everything else
  // (Budget Allocation, Budget Summary, the chart) is a computed,
  // read-only display, which fmt() above already comma-formats fine.
  // These can't just call fmt() unconditionally though: while the user
  // has one of them focused and is actively typing, a live "10,000.00"
  // re-format on every keystroke fights typing decimals (the "." gets
  // dropped the instant Number() rounds it away) and constantly resets
  // the cursor to the end. So each field only shows its comma-formatted
  // value while NOT focused; while focused it shows the plain number,
  // same as before this change, so typing still works exactly as it did.
  // FIX: e.target.select() here used to run BEFORE the value swapped from
  // its comma-formatted display (e.g. "0.00") to the plain number ("0")
  // -- setFocusedCostField's re-render (which is what actually changes
  // the input's value) happens asynchronously after this handler
  // returns, so selecting immediately just selected the old formatted
  // text, and React swapping the value out from under it right after
  // collapsed that selection to nothing. Deferring the select() with a
  // 0ms timeout lets the re-render (and the value change) land first, so
  // the now-plain "0" is what actually ends up selected -- restoring the
  // original "click in and just start typing to overwrite the 0" behavior.
  const [focusedCostField, setFocusedCostField] = useState(null);
  const handleCostFocus = (field) => (e) => {
    const input = e.target;
    setFocusedCostField(field);
    setTimeout(() => input.select(), 0);
  };
  const handleCostBlur = () => setFocusedCostField(null);
  const displayCost = (field, num) => (focusedCostField === field ? String(num) : fmt(num));

  // FIX: Budget / Design / Supervision / Other Project had no upper bound,
  // so typing a very long string of digits (e.g. holding down a key) fed
  // straight into Subtotal / Vat_Amount / Total_Sum below. Once one of
  // those sums reaches 1e21, JS's Number.toFixed(2) (used by fmt()) stops
  // returning a normal decimal string and switches to exponential
  // notation instead -- exactly the runaway-value display seen on both
  // this page and the Estimation step. Same cap as Step5Final.jsx's
  // Design/Supervision/Other Project fields: 999,999,999,999.99 SAR,
  // comfortably above any realistic project cost.
  const MAX_COST_VALUE = 999999999999.99;
  const clampCost = (raw) => {
    const num = Number(raw);
    if (!Number.isFinite(num)) return 0;
    if (num > MAX_COST_VALUE) return MAX_COST_VALUE;
    if (num < 0) return 0;
    return num;
  };

  return (
    <>
      {/* Was a plain "Start Cost Estimation" title -- replaced with the
          actual project name in a white block, same as Step4SubElement.jsx
          / Step5Final.jsx. */}
      <div className="page-title-head d-flex align-items-center">
        <div className="flex-grow-1">
          {/* Per follow-up request, recolored: non-current segments +
              "/" separators now use #02bc9c blended 90% toward white (a
              light mint tint, replacing the earlier #788ca5), and the
              current segment now uses the full #02bc9c (replacing the
              earlier #124d52) -- matching the reference design's
              light-inactive/vivid-active contrast. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Project</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              {step1.projectName || "—"}
            </span>
          </nav>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              {/* Wizard progress bar -- see the note above WIZARD_STEPS in
                  Step1ProjectInfo.jsx for why this is a fixed percentage
                  per page instead of the theme's JS-driven one. */}
              <div className="progress mb-4" style={{ height: 6 }}>
                <div className="progress-bar wizard-progress-bar" style={{ width: "29%" }} />
              </div>

              {/* Step indicator -- see the note above WIZARD_STEPS */}
              <ul className="nav nav-tabs wizard-tabs w-100 row g-2 border-0" role="tablist">
                {WIZARD_STEPS.map((step, idx) => (
                  <li className="nav-item col-12 col-lg" key={step.key}>
                    <span
                      className={`nav-link h-100${
                        idx < CURRENT_STEP_INDEX
                          ? " wizard-item-done"
                          : idx === CURRENT_STEP_INDEX
                          ? " wizard-item-current"
                          : " disabled"
                      }`}
                    >
                      <span className="d-flex align-items-center">
                        {step.key === "budget" ? (
                          <svg
                            className="flex-shrink-0"
                            style={{ width: "32px", height: "32px" }}
                            viewBox="0 0 917 1025"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path fillRule="evenodd" d="M 916 834 L 570 908 L 550 964 L 542 1001 L 540 1024 L 884 951 L 893 932 L 904 899 L 912 865 Z M 646 54 L 539 144 L 539 473 L 435 496 L 430 1 L 324 89 L 324 518 L 83 570 L 51 682 L 323 629 L 324 767 L 32 830 L 0 946 L 310 881 L 346 863 L 428 746 L 437 604 L 539 584 L 539 803 L 885 730 L 916 614 L 646 670 L 646 561 L 885 509 L 916 397 L 646 449 Z" />
                          </svg>
                        ) : (
                          <i className={`ti ${step.icon} fs-32 flex-shrink-0`} />
                        )}
                        <span className="flex-grow-1 ms-2 text-truncate">
                          <span className="mb-0 lh-base d-block fw-semibold text-body fs-base">{step.label}</span>
                          <span className="fs-xxs mb-0">{step.sub}</span>
                        </span>
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              <hr className="mt-3" />

              {serverError && (
                <div className="alert alert-danger" role="alert">
                  {serverError}
                </div>
              )}

              {/* ===================== PROPOSED COST ===================== */}
              <h5 className="text-green mb-3 fs-4 fw-bold">Proposed Cost</h5>

              <div className="row g-3 mb-4">
                <div className="col-xl-4">
                  <div className="card h-100 border">
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                        <span className="fw-bold">General Costs</span>
                        <span className="fw-bold">Cost ({currencySymbol})</span>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Execution Cost</label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={displayCost("Budget", values.Budget)}
                            onFocus={handleCostFocus("Budget")}
                            onBlur={handleCostBlur}
                            onChange={(e) => setValues({ ...values, Budget: clampCost(e.target.value.replace(/,/g, "")) })}
                          />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Design Cost</label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={displayCost("Design", values.Design)}
                            onFocus={handleCostFocus("Design")}
                            onBlur={handleCostBlur}
                            onChange={(e) => setValues({ ...values, Design: clampCost(e.target.value.replace(/,/g, "")) })}
                          />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Supervision Cost</label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={displayCost("Supervision", values.Supervision)}
                            onFocus={handleCostFocus("Supervision")}
                            onBlur={handleCostBlur}
                            onChange={(e) => setValues({ ...values, Supervision: clampCost(e.target.value.replace(/,/g, "")) })}
                          />
                        </div>
                      </div>

                      <div className="row align-items-center">
                        <label className="col-sm-4 col-form-label">Other Cost</label>
                        <div className="col-sm-8">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control"
                            value={displayCost("Other_Project", values.Other_Project)}
                            onFocus={handleCostFocus("Other_Project")}
                            onBlur={handleCostBlur}
                            onChange={(e) => setValues({ ...values, Other_Project: clampCost(e.target.value.replace(/,/g, "")) })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-8">
                  <div className="border card h-100">
                    <div className="card-body">
                      <div className="border-bottom fw-bold mb-3 pb-2">Cost Factors (%)</div>
                      <div className="row g-3">
                        {/* Duration -- per request, a real Duration_Master
                            dropdown here now (same pattern as Step5Final.jsx),
                            not a read-only display of Step 1's saved value:
                            Step 1's own Duration field was removed. It has
                            no percent/amount pair like the FactorRow items
                            below, so it's custom JSX instead of going
                            through that component. */}
                        <div className="col-md-6">
                          <div className="row align-items-center g-2">
                            <label className="col-6 col-md-3 col-form-label">Duration</label>
                            <div className="col-12 col-md-9">
                              <select
                                className="form-select"
                                value={duration}
                                onChange={(e) => setDuration(e.target.value)}
                              >
                                <option value="">Select</option>
                                {durations.map((d) => (
                                  <option key={d.D_ID} value={d.Duration}>
                                    {d.Duration}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        <FactorRow label="Contingency" value={values.Contingency_Percent} onChange={handlePercentChange("Contingency_Percent")} amount={values.Contingency_Amount} fmt={fmt} />
                        <FactorRow label="Prelims" value={values.Prelims_Percent} onChange={handlePercentChange("Prelims_Percent")} amount={values.Prelims_Amount} fmt={fmt} />
                        <FactorRow label="Location" value={values.Location_Percent} onChange={handlePercentChange("Location_Percent")} amount={values.Location_Amount} fmt={fmt} />
                        <FactorRow label="Risk" value={values.Risk_Percent} onChange={handlePercentChange("Risk_Percent")} amount={values.Risk_Amount} fmt={fmt} />
                        <FactorRow label="Tender Index" value={values.Tender_Percent} onChange={handlePercentChange("Tender_Percent")} amount={values.Tender_Amount} fmt={fmt} />
                        <FactorRow label="Inflation" value={values.Inflation_Percent} onChange={handlePercentChange("Inflation_Percent")} amount={values.Inflation_Amount} fmt={fmt} />
                        <FactorRow label="Escalation" value={values.Escalation_Percent} onChange={handlePercentChange("Escalation_Percent")} amount={values.Escalation_Amount} fmt={fmt} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ===================== COST BREAKDOWN ===================== */}
              <h5 className="text-green mb-3 mt-4 fs-4 fw-bold">Cost Breakdown</h5>

              <div className="row g-3">
                <div className="col-xl-5">
                  <div className="card h-100 border">
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                        <span className="fw-bold">Cost Allocation</span>
                        <span className="fw-bold">Cost ({currencySymbol})</span>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Execution Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(values.Construction)} />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Cost Factors</label>
                        <div className="col-sm-6">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(values.Factor_Cost)} />
                        </div>
                        <div className="col-sm-2 text-end ps-0">
                          <button
                            type="button"
                            className="btn btn-link p-0 fw-semibold text-decoration-none"
                            onClick={() => setShowFactorView((v) => !v)}
                          >
                            View
                          </button>
                        </div>
                      </div>

                      {showFactorView && (
                        <div className="rounded border bg-light-subtle p-2 mb-3 fs-13">
                          <div className="d-flex justify-content-between"><span>Prelims</span><span>{fmt(values.Prelims_Amount)}</span></div>
                          <div className="d-flex justify-content-between"><span>Risk</span><span>{fmt(values.Risk_Amount)}</span></div>
                          <div className="d-flex justify-content-between"><span>Inflation</span><span>{fmt(values.Inflation_Amount)}</span></div>
                          <div className="d-flex justify-content-between"><span>Contingency</span><span>{fmt(values.Contingency_Amount)}</span></div>
                          <div className="d-flex justify-content-between"><span>Location</span><span>{fmt(values.Location_Amount)}</span></div>
                          <div className="d-flex justify-content-between"><span>Tender Index</span><span>{fmt(values.Tender_Amount)}</span></div>
                          <div className="d-flex justify-content-between"><span>Escalation</span><span>{fmt(values.Escalation_Amount)}</span></div>
                        </div>
                      )}

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Design Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(values.Design)} />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Supervision Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(values.Supervision)} />
                        </div>
                      </div>

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label">Other Cost</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light" readOnly value={fmt(values.Other_Project)} />
                        </div>
                      </div>

                      <hr className="my-3" />

                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label fw-bold">Sub Total</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light fw-bold" readOnly value={fmt(values.Subtotal)} />
                        </div>
                      </div>

                      {/* Per request, VAT is no longer inside the Cost
                          Factors (%) card above -- it's its own column
                          here instead: user picks the % first (dropdown,
                          sourced from Vat_Config via vatOptions), then the
                          computed amount shows next to it. */}
                      <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label fw-bold">VAT</label>
                        <div className="col-sm-4">
                          <select
                            className="form-select fw-bold"
                            value={values.VAT_Percent}
                            onChange={(e) => setValues({ ...values, VAT_Percent: Number(e.target.value) })}
                          >
                            {vatOptions.map((opt) => (
                              <option key={opt.VAT_ID} value={opt.VAT_Percent}>
                                {opt.VAT_Percent}%
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-sm-4">
                          <input type="text" className="form-control bg-light fw-bold" readOnly value={fmt(values.Vat_Amount)} />
                        </div>
                      </div>

                      <div className="row align-items-center">
                        <label className="col-sm-4 col-form-label fw-bold">Total Sum</label>
                        <div className="col-sm-8">
                          <input type="text" className="form-control bg-light fw-bold" readOnly value={fmt(values.Total_Sum)} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-7">
                  <div className="card h-100 border">
                    <div className="card-body">
                      <h6 className="fw-bold mb-3">Proposed Cost Summary</h6>

                      <div className="row g-2 mb-3">
                        {/* Project Duration leads the tile row (matching the
                            mockup), in a distinct light-blue so it reads as
                            informational rather than another cost figure --
                            in Years rather than the raw "36 Months" string. */}
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#4aa3df" }}>
                            <div className="fw-bold fs-18">{formatDurationYears(duration)}</div>
                            <div className="fs-12">Project Duration</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#8b7fd4" }}>
                            <div className="fw-bold fs-18">{fmtMillions(values.Subtotal)}</div>
                            <div className="fs-12">Sub Total</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#17a76b" }}>
                            <div className="fw-bold fs-18">{fmtMillions(values.Vat_Amount)}</div>
                            <div className="fs-12">VAT</div>
                          </div>
                        </div>
                        <div className="col-3">
                          <div className="rounded text-center text-white p-2" style={{ background: "#eda100" }}>
                            <div className="fw-bold fs-18">{fmtMillions(values.Total_Sum)}</div>
                            <div className="fs-12">Total Sum</div>
                          </div>
                        </div>
                      </div>

                      <div style={{ height: 430 }}>
                        <Bar data={chartData} options={chartOptions} plugins={[barValueLabelPlugin]} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column flex-sm-row justify-content-sm-between gap-2 mt-4">
                <button type="button" className="btn btn-outline-primary" onClick={() => navigate(ROUTES.COST_ESTIMATION_STEP1)}>
                  ← Back
                </button>
                <div className="d-flex flex-column flex-sm-row align-items-sm-center gap-2">
                  {/* Per request, same "Last saved <time>" convention as
                      Step5Final.jsx, now shown next to Save on every
                      wizard page. */}
                  {lastSavedTime && (
                    <span className="text-muted fs-13">
                      Last saved {lastSavedTime.toLocaleTimeString()}
                    </span>
                  )}
                  {/* Per explicit request, reverted back to: "Save"
                      (left) saves and stays on this page, updating "Last
                      saved" above -- no popup. "Start Estimation" (right)
                      alerts the result and navigates to Step 3. */}
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    disabled={loading}
                    onClick={async () => {
                      if (loading) return;
                      await saveBudget("save");
                    }}
                  >
                    {loading ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading}
                    onClick={async () => {
                      if (loading) return;
                      // Per request: Start Estimation doesn't touch the
                      // Save button's "Last saved" feedback at all -- clear
                      // any stale value from an earlier Save click so it
                      // doesn't still show while this modal-and-navigate
                      // flow runs.
                      setLastSavedTime(null);
                      await saveBudget("next");
                    }}
                  >
                    {loading ? "Processing..." : "Start Estimation →"}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Themed success confirmation -- same dark-teal modal markup/
          colors used across the wizard (e.g. Step4SubElement.jsx's "Cost
          item inserted successfully" popup), so this looks consistent
          instead of a plain OS-styled "localhost:3000 says" box. */}
      {showSavedModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ background: "rgba(0,0,0,0.75)" }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="border-0 modal-content text-white" style={{ backgroundColor: "#124d52", borderRadius: 4 }}>
              <div className="text-center modal-body p-4">
                <div className="mb-3">
                  <div className="align-items-center justify-content-center bg-white d-inline-flex rounded-circle" style={{ width: 48, height: 48 }}>
                    <i className="fs-2 text-dark ti ti-circle-check" style={{ fontStyle: "normal", fontWeight: 700 }} />
                  </div>
                </div>
                <h5 className="fs-5 fw-normal mb-4 modal-title">{savedModalMessage}</h5>
                <div className="d-flex justify-content-center gap-3">
                  <button type="button" className="btn btn-primary" onClick={handleSavedModalOk}>
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}